import { lazy, Suspense, type ReactNode } from 'react'
import { useIsModuleEnabled } from '@features/modules/hooks/useIsModuleEnabled'
import { ErrorBoundary } from '@features/shared/components/ErrorBoundary'
import { usePanelStack } from '../hooks/usePanelStack'
import {
  CHAT_PANEL_MAX_WIDTH_PX,
  CHAT_PANEL_MIN_WIDTH_PX,
  loadChatPanel,
} from '../logic/chatPanelChunk'
import SidePanelShell from './SidePanelShell'
import SupportPanel from './SupportPanel'

const ChatPanel = lazy(loadChatPanel)

/** The chat panel's frame, open at once while its code loads (or if it can't). */
function ChatPanelFrame({ onClose, children }: { onClose: () => void; children?: ReactNode }) {
  return (
    <SidePanelShell
      title="AI Assistant"
      onClose={onClose}
      minWidthPx={CHAT_PANEL_MIN_WIDTH_PX}
      maxWidthPx={CHAT_PANEL_MAX_WIDTH_PX}
    >
      {children}
    </SidePanelShell>
  )
}

/**
 * In-flow panel region mounted beside route content. Open panels consume
 * horizontal layout space so the active page resizes instead of being covered.
 * Replaced panels stay mounted but hidden so their state survives.
 */
export default function GlobalPanelStack() {
  const {
    chatSession,
    isChatOpen,
    isChatMounted,
    closeChat,
    isSupportOpen,
    isSupportMounted,
    closeSupport,
  } = usePanelStack()
  const isAiEnabled = useIsModuleEnabled('ai')
  const showChat = isChatMounted && isAiEnabled

  if (!showChat && !isSupportMounted) return null

  return (
    <div className="flex h-full shrink-0 flex-row-reverse" data-panel-stack>
      {isSupportMounted && (
        <div className={isSupportOpen ? 'contents' : undefined} hidden={!isSupportOpen}>
          <SupportPanel onClose={closeSupport} open={isSupportOpen} />
        </div>
      )}
      {showChat && (
        <div className={isChatOpen ? 'contents' : undefined} hidden={!isChatOpen}>
          <ErrorBoundary
            fallback={
              <ChatPanelFrame onClose={closeChat}>
                <p className="px-4 py-3 text-sm text-muted">
                  The AI Assistant didn't load. Reload the page to try again.
                </p>
              </ChatPanelFrame>
            }
          >
            <Suspense fallback={<ChatPanelFrame onClose={closeChat} />}>
              <ChatPanel
                key={chatSession?.key ?? 0}
                onClose={closeChat}
                open={isChatOpen}
                threadId={chatSession?.threadId}
                initialDraft={chatSession?.draft}
              />
            </Suspense>
          </ErrorBoundary>
        </div>
      )}
    </div>
  )
}
